import { useContext } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { KBContext, SearchContext } from "../../Context";
import rightIcon from "../../images/rightIcon.svg";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch";
import { KB_WEBSITE_GAINSIGHT_EVENTS, sendGainsightEvent } from "../../gainSightIntegration";
import { createInertElement, escapeHtml } from "../../Constants/security";

/* Figma: clean search results — "N Results" header, divider, list of breadcrumb + blue title + snippet */

const PageWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  margin-top: 32px;
  box-sizing: border-box;
  overflow: hidden;
  font-family: Inter, sans-serif;

  @media screen and (max-width: 778px) {
    margin-top: 24px;
  }

  @media screen and (max-width: 500px) {
    margin-top: 16px;
  }
`;

const BreadCrumContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 20px 0;
  font-family: Inter, sans-serif;

  @media screen and (max-width: 500px) {
    margin-bottom: 16px;
  }
`;

const BreadCrum = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475569;
  margin-right: 8px;
  cursor: pointer;
`;

const RightImage = styled.img`
  height: 9.55px;
  width: 5.83px;
  margin-right: 8px;
`;

const CountHeader = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #2d2d2d;
  margin: 0 0 4px 0;
`;

const Divider = styled.hr`
  margin: 4px 0 0 0;
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 20px;
  width: 100%;
`;

const ResultBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;

  &:hover .result-title {
    text-decoration: underline;
  }
`;

const ResultBreadcrumb = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #898989;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
`;

const BreadcrumbSegment = styled.span`
  cursor: ${(p) => (p.$clickable ? "pointer" : "inherit")};
  &:hover {
    text-decoration: ${(p) => (p.$clickable ? "underline" : "none")};
  }
`;

const ResultTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #0C3E9D;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 960px;

  &.result-title strong,
  &.result-title b {
    font-weight: 400;
    color: #0C3E9D;
  }
`;

const ResultSnippet = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #525252;
  max-width: 960px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;

  strong,
  b {
    font-weight: 500;
    color: #2d2d2d;
  }
`;

const SubmitIssueText = styled.span`
  font-family: Inter, sans-serif;
  color: ${(props) => props.color};
  font-weight: 500;
  cursor: pointer;
`;

const NoResults = styled.div`
  font-family: Inter, sans-serif;
  margin-top: 16px;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #64748b;
`;

function extractContent(s, space) {
  // Parsed inside an inert document: no scripts execute and no sub-resources load.
  const span = createInertElement("span", s);
  if (space) {
    const children = span.querySelectorAll("*");
    for (let i = 0; i < children.length; i++) {
      if (children[i].textContent) children[i].textContent += " ";
      else children[i].innerText += " ";
    }
  }
  return [span.textContent || span.innerText].toString().replace(/ +/g, " ");
}

/**
 * Strip API metadata prefix and show only page content snippet.
 * Removes [Category]: ... [Title]: ... and dash separators; strips markdown images.
 */
function getSnippetOnly(contentPlain) {
  if (!contentPlain || typeof contentPlain !== "string") return contentPlain || "";
  let s = contentPlain;
  const dashBlock = /-{5,}/;
  const dashIdx = s.search(dashBlock);
  if (dashIdx !== -1) {
    s = s.slice(dashIdx).replace(dashBlock, "").trim();
  } else {
      s = s.replace(/^\s*\[Category\]:[^[]*(?:\[Title\]:[^[]*)?/i, "").trim();
  }
  s = s.replace(/!\[\s*\]\s*\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
  return s || contentPlain;
}

/** Get category slug from article (fullPath is e.g. "/cat-slug/article-slug"). */
function getCategorySlug(item) {
  if (item?.categorySlug) return item.categorySlug;
  const pathParts = (item?.fullPath || "").split("/").filter(Boolean);
  return pathParts.length >= 2 ? pathParts[0] : null;
}

/** Segments for breadcrumb: { label, path } path is null for non-clickable. */
function buildBreadcrumbSegments(item) {
  const segments = [{ label: "Home", path: "/" }];
  const categorySlug = getCategorySlug(item);
  if (item?.categoryTitle && categorySlug) {
    segments.push({ label: item.categoryTitle, path: `/${categorySlug}` });
  } else {
    segments.push({ label: "Search results", path: null });
  }
  return segments;
}

function highlightQuery(text, query) {
  if (!query || !text) return escapeHtml(text);
  const words = query
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 2);
  if (!words.length) return escapeHtml(text);
  const regex = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const parts = text.split(regex);
  // Escape every segment: the <strong> highlight is the only markup we emit,
  // so titles/snippets can never inject HTML through dangerouslySetInnerHTML.
  return parts.map((part, i) =>
    i % 2 === 1 && part ? `<strong>${escapeHtml(part)}</strong>` : escapeHtml(part)
  ).join("");
}

function App() {
  const { searchList, setSearchInput, searchInput, setSearchList } =
    useContext(SearchContext);
  const { data } = useContext(KBContext);
  const { pathname } = useLocation();
  const navigate = useNavigateWithSearch();

  const renderCount = () => {
    const list = Array.isArray(searchList) ? searchList : [];
    if (list.length === 0) {
      return <>No results for "{searchInput}"</>;
    }
    if (list.length === 1) {
      return <>1 Result</>;
    }
    return <>{list.length} Results</>;
  };

  const handleSearchResultClick = (item, index) => {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_RESULT_CLICKED, {
      search_query: searchInput,
      clicked_article_slug: item.slug,
      clicked_article_title: extractContent(item?.title, true),
      result_position: index + 1,
    });
    setSearchInput("");
    const path = item.fullPath || (getCategorySlug(item) ? `/${getCategorySlug(item)}/${item.slug}` : null);
    navigate(path || `/article/${item.slug}`);
  };

  const handleBreadcrumbClick = (e, path) => {
    e.stopPropagation();
    if (!path) return;
    setSearchInput("");
    setSearchList(false);
    navigate(path);
  };

  return (
    <PageWrapper bannerLeft={pathname.slice(1) === ""}>
      <BreadCrumContainer>
        <BreadCrum
          onClick={() => {
            setSearchInput("");
            setSearchList(false);
            navigate("/");
          }}
        >
          Home
        </BreadCrum>
        <RightImage src={rightIcon} alt="righticon" />
        <BreadCrum>Search results</BreadCrum>
      </BreadCrumContainer>

      <CountHeader>{renderCount()}</CountHeader>
      {Array.isArray(searchList) && searchList.length > 0 && <Divider />}

      {(!Array.isArray(searchList) || searchList.length === 0) && (
        <NoResults>
          {data?.enableHelpForm ? (
            <>
              Please{" "}
              <SubmitIssueText
                color={data.brandColor}
                onClick={() => navigate("/submit-issue")}
              >
                submit an Issue
              </SubmitIssueText>{" "}
              if you can't find what you are looking for
            </>
          ) : (
            <>We couldn't find what you are looking for.</>
          )}
        </NoResults>
      )}

      {Array.isArray(searchList) && searchList.length > 0 && (
        <ResultsList>
          {searchList.map((item, index) => {
            const breadcrumbSegments = buildBreadcrumbSegments(item);
            const titlePlain = extractContent(item?.title, true);
            const contentPlain = extractContent(item?.content, true);
            const snippetPlain = getSnippetOnly(contentPlain);
            const titleWithHighlight = highlightQuery(titlePlain, searchInput);
            const snippetWithHighlight = highlightQuery(snippetPlain, searchInput);

            return (
              <ResultBlock
                key={item.articleId}
                onClick={() => handleSearchResultClick(item, index)}
              >
                <ResultBreadcrumb>
                  {breadcrumbSegments.map((seg, i) => (
                    <span key={i}>
                      {i > 0 && " > "}
                      <BreadcrumbSegment
                        $clickable={!!seg.path}
                        onClick={(e) => handleBreadcrumbClick(e, seg.path)}
                      >
                        {seg.label}
                      </BreadcrumbSegment>
                    </span>
                  ))}
                </ResultBreadcrumb>
                <ResultTitle
                  className="result-title"
                  dangerouslySetInnerHTML={{ __html: titleWithHighlight }}
                />
                <ResultSnippet
                  dangerouslySetInnerHTML={{ __html: snippetWithHighlight }}
                />
              </ResultBlock>
            );
          })}
        </ResultsList>
      )}
    </PageWrapper>
  );
}

export default App;
